<script lang="ts" setup>
import EventItemForm from '@/components/events/EventItemForm.vue';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { computed, markRaw, onBeforeUnmount, onMounted, Ref, ref } from 'vue';
import { useStore } from 'vuex';
import { format } from 'date-fns';
import { EventsActions, EventsState } from '@/stores/events.store';
import { EventItem } from '@/models/event-item.model';
import { useEvents, Event } from '@/services/events.composable';
import TranslatedState from '../common/TranslatedState.vue';
import ListFilter from '../common/ListFilter.vue';
import eventsService from '@/services/events.service';

const store = useStore<ShiftCMSState & { ev: EventsState }>();
const items = computed<EventItem[]>(() => store.state.ev.items);
const events = useEvents();

const update = () => {
  store.dispatch(EventsActions.GetEventItems);
};

onMounted(() => {
  update();
  events.on(Event.LanguageChanged, update);
});

onBeforeUnmount(() => {
  store.commit(Mutations.HideDrawer);
  events.off(Event.LanguageChanged, update);
});

const editEventItem = (_: Event, { item }: { item: EventItem }) => {
  store.commit(Mutations.ShowDrawer, {
    component: markRaw(EventItemForm),
    data: item,
  });
};

const addEventItem = () => {
  store.commit(Mutations.ShowDrawer, { component: markRaw(EventItemForm) });
};

const headers = [
  { title: 'ID', key: 'id', sortable: false, width: '5%' },
  { title: 'Title', key: 'title', width: '30%' },
  { title: 'Category', key: 'mainCategory.title', width: '15%' },
  { title: 'Domain', key: 'url', width: '15%' },
  { title: 'Start', key: 'startDate', width: '15%' },
  { title: 'Added', key: 'creationDate', width: '10%' },
  { title: 'State', key: 'translated', width: '5%' },
];

const formatUrl = (url: string) => {
  const data = new URL(url);

  return data.host;
};

const filter = ref<string>('');
const filterTitle = ref<string>('');
const filteredItems = computed<EventItem[]>(() => {
  return items.value.filter((v: EventItem) => {
    if (filterTitle.value) {
      return [
        v.title,
        v.description,
        v.startDate ? format(v.startDate, 'do MMMM - HH:mm') : '',
        format(v.creationDate, 'dd-MM-yyyy'),
      ]
        .map((s) => s.toLowerCase())
        .join('')
        .includes(filterTitle.value);
    }

    return true;
  });
});

function updateFilter(filters: { filter: string; filterTitle: string }) {
  filter.value = filters.filter;
  filterTitle.value = filters.filterTitle;
}

async function triggerCrawl(isActive: Ref) {
  store.dispatch(Actions.ShowNotification, {
    message: 'Crawling sources',
    type: 'info',
  });

  isActive.value = false;
  const result = await eventsService.trigger();

  store.dispatch(Actions.ShowNotification, {
    message: `Added ${result.totalInserted} events from ${result.totalSources} sources`,
    type: 'info',
  });
}

async function triggerCrawlAndUpdate(isActive: Ref) {
  store.dispatch(Actions.ShowNotification, {
    message: 'Crawling sources',
    type: 'info',
  });

  isActive.value = false;
  const result = await eventsService.trigger(true);

  store.dispatch(Actions.ShowNotification, {
    message: `Added ${result.totalInserted} events from ${result.totalSources} sources`,
    type: 'info',
  });
}
</script>

<template>
  <v-container>
    <h2>Event Items</h2>
    <v-btn
      type="button"
      color="warning"
      @click="triggerCrawl"
      style="position: absolute; top: 70px; right: 60px; z-index: 1"
      >Find more events</v-btn
    >

    <v-dialog width="auto">
      <template v-slot:activator="{ props: activatorProps }">
        <v-btn
          type="button"
          color="warning"
          v-bind="activatorProps"
          style="position: absolute; top: 70px; right: 60px; z-index: 1"
          >Find more events</v-btn
        >
      </template>
      <template v-slot:default="{ isActive }">
        <v-card>
          <v-card-text>
            <h2>Crawl the internet for more events</h2>

            <v-row>
              <v-col>
                <v-btn
                  class="mt-2"
                  color="success"
                  type="button"
                  block
                  @click="() => triggerCrawl(isActive)"
                  >Find new events</v-btn
                ></v-col
              >
            </v-row>
            <v-row>
              <v-col
                ><v-btn
                  class="mt-2"
                  color="warning"
                  type="button"
                  block
                  @click="() => triggerCrawlAndUpdate(isActive)"
                  >Update and find new events</v-btn
                ></v-col
              >
            </v-row>
          </v-card-text>
        </v-card>
      </template>
    </v-dialog>
    <ListFilter dense :options="[]" @filter="updateFilter" />
    <v-data-table
      density="compact"
      @click:row="editEventItem"
      :headers="headers"
      :items="filteredItems"
      itemsPerPage="25"
      hover
      :rowProps="
        (data) => {
          return {
            class: {
              'item-published': data.item.published,
              'item-dismissed': data.item.dismissed,
            },
          };
        }
      "
    >
      <template v-slot:item.startDate="{ value }">
        {{ format(value, 'do MMMM - HH:mm') }}
      </template>
      <template v-slot:item.creationDate="{ value }">
        {{ format(value, 'dd-MM-yyyy') }}
      </template>
      <template v-slot:item.url="{ value }">
        {{ formatUrl(value) }}
      </template>
      <template v-slot:item.translated="{ item }">
        <div style="display: flex; flex-direction: row">
          <translated-state :item="item" />
          &nbsp;
          <v-tooltip
            :text="
              item.published
                ? 'This item is published'
                : 'This item is not published'
            "
          >
            <template v-slot:activator="{ props }">
              <v-icon
                v-bind="props"
                :icon="item.published ? 'mdi-eye-check' : 'mdi-eye-outline'"
                :color="item.published ? 'green' : 'red'"
              ></v-icon>
            </template>
          </v-tooltip>
        </div>
      </template>
    </v-data-table>

    <v-btn @click="addEventItem" color="success">Add Event Item</v-btn>&nbsp;
  </v-container>
</template>
<style>
.item-dismissed {
  opacity: 0.5;
  text-decoration: line-through;
}
.item-published {
  font-weight: bold;
}
</style>
